import { useState, useEffect } from "react";
import {
  Flex,
  Text,
  Card,
  Button,
  Select,
  TextArea,
  Switch,
  Badge,
  Callout,
  AlertDialog,
  TextField,
  IconButton,
  DropdownMenu,
  Separator,
  Checkbox,
  Tooltip,
  Progress,
} from "@radix-ui/themes";
import { InfoCircledIcon, PlusIcon, ChevronDownIcon, ChevronUpIcon, DotsHorizontalIcon } from "@radix-ui/react-icons";
import { useProfileContext } from "context";
import { useGetAISettings } from "hooks/b_reviews/useGetAISettings";
import { useSaveAISettings, useCreateAISetting, useDeleteAISetting } from "hooks/b_reviews/useSaveAISettings";
import { ToastAlert } from "components";
import { useToast } from "../../context/ToastContext";

// Default values for new settings
const DEFAULT_VALUES = {
  auto_reply_enabled: false,
  auto_reply_ratings: [1,2,3,4,5],
  tone_control: "friendly",
  persona: "owner",
  default_length: "standard",
  greeting_format: "Hi {{reviewer_first_name}},",
  signature_format: "Kind regards,\n{{business_name}} Team",
  negative_review_apology: "We're truly sorry to hear about your experience. This isn't the standard we aim for, and we appreciate you bringing it to our attention.",
  additional_instructions: "",
};

export default function ReviewsAISettings() {
  const { userProfile } = useProfileContext();
  const organisationId = userProfile?.organisation_id;
  const { setToastMessage } = useToast();

  const { data: settingsResponse, isLoading, error, refetch } = useGetAISettings(organisationId);
  const saveSettings = useSaveAISettings();
  const createSetting = useCreateAISetting();
  const deleteSetting = useDeleteAISetting();

  // Extract data from response
  const settings = settingsResponse?.data?.settings || [];
  const availableLocations = settingsResponse?.data?.available_locations || [];
  const assignedLocationIds = settingsResponse?.data?.assigned_location_ids || [];

  // State for tracking form data for each setting card
  const [formStates, setFormStates] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // State for expanded/collapsed cards
  const [expandedCards, setExpandedCards] = useState({});
  
  // State for new setting form
  const [newSettingName, setNewSettingName] = useState("");
  const [showNewSettingForm, setShowNewSettingForm] = useState(false);

  // Toggle card expansion
  const toggleCardExpansion = (settingId) => {
    setExpandedCards(prev => ({
      ...prev,
      [settingId]: !prev[settingId]
    }));
  };

  // Initialize form states when settings load
  useEffect(() => {
    if (settings.length > 0) {
      const initialStates = {};
      settings.forEach(setting => {
        initialStates[setting.id] = {
          name: setting.name,
          location_ids: setting.location_ids || [],
          auto_reply_enabled: setting.auto_reply_enabled,
          auto_reply_ratings: setting.auto_reply_ratings || [1,2,3,4,5],
          tone_control: setting.tone_control,
          persona: setting.persona || "owner",
          default_length: setting.default_length,
          greeting_format: setting.greeting_format,
          signature_format: setting.signature_format,
          negative_review_apology: setting.negative_review_apology,
          additional_instructions: setting.additional_instructions || "",
        };
      });
      setFormStates(initialStates);
    }
  }, [settings]);

  // Get available locations for a specific setting (unassigned + currently assigned to this setting)
  const getAvailableLocationsForSetting = (settingId) => {
    const currentSetting = settings.find(s => s.id === settingId);
    const currentLocationIds = currentSetting?.location_ids || [];
    
    return availableLocations.filter(loc => {
      return !assignedLocationIds.includes(loc.id) || currentLocationIds.includes(loc.id);
    });
  };

  // Check if a location is assigned to another setting (not the current one)
  const isLocationAssignedToOther = (locationId, currentSettingId) => {
    const currentSetting = settings.find(s => s.id === currentSettingId);
    const currentLocationIds = currentSetting?.location_ids || [];
    
    // If location is in assignedLocationIds but NOT in current setting's locations, it's assigned elsewhere
    return assignedLocationIds.includes(locationId) && !currentLocationIds.includes(locationId);
  };

  // Get the name of the setting that a location is assigned to
  const getAssignedSettingName = (locationId, currentSettingId) => {
    for (const setting of settings) {
      if (setting.id !== currentSettingId && setting.location_ids?.includes(locationId)) {
        return setting.name;
      }
    }
    return null;
  };

  // Toggle location selection for a setting
  const toggleLocationSelection = (settingId, locationId) => {
    const currentIds = formStates[settingId]?.location_ids || [];
    const newIds = currentIds.includes(locationId)
      ? currentIds.filter(id => id !== locationId)
      : [...currentIds, locationId];
    updateFormState(settingId, "location_ids", newIds);
  };

  // Update form state for a specific setting
  const updateFormState = (settingId, field, value) => {
    setFormStates(prev => ({
      ...prev,
      [settingId]: {
        ...prev[settingId],
        [field]: value,
      },
    }));
  };

  // Toggle a star rating in auto_reply_ratings for a setting
  const toggleAutoReplyRating = (settingId, rating) => {
    const current = formStates[settingId] || {};
    const ratings = current.auto_reply_ratings || [];
    const newRatings = ratings.includes(rating) ? ratings.filter(r => r !== rating) : [...ratings, rating];
    updateFormState(settingId, 'auto_reply_ratings', newRatings);
  };

  // Check if a setting has unsaved changes
  const hasChanges = (settingId) => {
    const original = settings.find(s => s.id === settingId);
    const current = formStates[settingId];
    if (!original || !current) return false;

    return (
      current.name !== original.name ||
      JSON.stringify(current.location_ids) !== JSON.stringify(original.location_ids || []) ||
      JSON.stringify(current.auto_reply_ratings || []) !== JSON.stringify(original.auto_reply_ratings || []) ||
      current.auto_reply_enabled !== original.auto_reply_enabled ||
      current.tone_control !== original.tone_control ||
      current.persona !== (original.persona || "owner") ||
      current.default_length !== original.default_length ||
      current.greeting_format !== original.greeting_format ||
      current.signature_format !== original.signature_format ||
      current.negative_review_apology !== original.negative_review_apology ||
      current.additional_instructions !== (original.additional_instructions || "")
    );
  };

  // Handle save for a specific setting
  const handleSave = async (settingId) => {
    const formData = formStates[settingId];
    if (!formData) return;

    setSavingId(settingId);
    try {
      await saveSettings.mutateAsync({
        organisation_id: organisationId,
        setting_id: settingId,
        ...formData,
      });
      setToastMessage("AI setting saved successfully!");
      refetch();
    } catch (err) {
      setToastMessage(err.response?.data?.message || "Failed to save AI setting");
    } finally {
      setSavingId(null);
    }
  };

  // Handle create new setting
  const handleCreateNew = async () => {
    if (!newSettingName.trim()) {
      setToastMessage("Please enter a name for the new AI setting");
      return;
    }

    setIsCreating(true);
    try {
      await createSetting.mutateAsync({
        organisation_id: organisationId,
        name: newSettingName.trim(),
        is_default: false,
        location_ids: [],
        ...DEFAULT_VALUES,
      });
      setToastMessage("AI setting created successfully!");
      setNewSettingName("");
      setShowNewSettingForm(false);
      refetch();
    } catch (err) {
      setToastMessage(err.response?.data?.message || "Failed to create AI setting");
    } finally {
      setIsCreating(false);
    }
  };

  // Handle delete
  const handleDelete = async (settingId) => {
    try {
      await deleteSetting.mutateAsync({
        settingId,
        organisationId,
      });
      setToastMessage("AI setting deleted successfully!");
      setDeleteConfirmId(null);
      refetch();
    } catch (err) {
      setToastMessage(err.response?.data?.message || "Failed to delete AI setting");
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        position: "fixed", 
        top: 0, 
        left: 0, 
        width: "100%", 
        zIndex: 1000 
      }}>
        <Progress size="1" duration="1" />
      </div>
    );
  }

  if (error) {
    return (
      <Flex align="center" justify="center" style={{ height: "300px" }}>
        <Text size="3" color="red">Failed to load settings. Please try again.</Text>
      </Flex>
    );
  }

  // Sort settings: default first, then by name
  const sortedSettings = [...settings].sort((a, b) => {
    if (a.is_default && !b.is_default) return -1;
    if (!a.is_default && b.is_default) return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <Flex direction="column" gap="4" style={{ width: "100%", height: "100%", overflowY: "auto", paddingBottom: "20px" }}>
      <ToastAlert />

      {/* Info Callout */}
      <Callout.Root color="amber" style={{ flexShrink: 0 }}>
        <Callout.Icon>
          <InfoCircledIcon />
        </Callout.Icon>
        <Callout.Text>
          Create multiple AI settings for different locations. Each location can have its own response style, tone, and auto-reply preferences.
          <br />
          <strong>Default setting:</strong> Used for locations not assigned to any custom setting.
          <br />
          <strong>Manual Generation:</strong> Use the "Generate with AI" button on any review to get AI suggestions you can edit before posting.
          <br />
          <strong>Auto-Reply:</strong> When enabled, new google reviews will automatically receive AI-generated responses based on the location's AI settings every <strong>6 hours</strong>.
        </Callout.Text>
      </Callout.Root>

      {/* Setting Cards */}
      {sortedSettings.map((setting) => {
        const formData = formStates[setting.id] || {};
        const isDefault = setting.is_default;
        const isExpanded = expandedCards[setting.id] ?? false;

        return (
          <Card key={setting.id} style={{ display: "flex", flexDirection: "column", flex: "0 0 auto", 
            alignItems: "flex-start", alignSelf: "stretch", gap: "var(--space-4)", padding: "var(--space-4)" }}>
            {/* Card Header */}
            <Flex gap="2" style={{ alignSelf: "stretch", alignItems: "center", justifyContent: "space-between" }}>
              {/* Setting Name */}
              <Flex direction="column" style={{ alignItems: "flex-start", width: "30%" }}>
                <Text size="3" weight="medium">{setting.name}</Text>
                <Text size="1" color="gray">
                  {isDefault ? "Default setting" : `${setting.locations_count || 0} location(s) assigned`}
                </Text>
              </Flex>

              {/* Information Badges */}
              <Flex gap="2" style={{ alignItems: "center", justifyContent: "space-between", width: "70%" }}>
                {/* Default Badge */}
                <Flex style={{ width: "100%", justifyContent: "center" }}>
                  {isDefault && (
                    <Badge size="1" variant="soft" color="blue">Default</Badge>
                  )}
                </Flex>
                {/* Auto-Reply Status */}
                <Flex style={{ width: "100%", justifyContent: "center" }}>
                  <Badge size="1" variant="soft" color={formData.auto_reply_enabled ? "green" : "gray"}>
                    {formData.auto_reply_enabled ? "Auto-reply on" : "Auto-reply off"}
                  </Badge>
                </Flex>
                {/* Tone */}
                <Flex style={{ width: "100%", justifyContent: "center" }}>
                  <Text size="1" color="gray" style={{ textTransform: "capitalize" }}>
                    {(formData.tone_control || "friendly").replace("_", " ")}
                  </Text>
                </Flex>
                {/* Actions */}
                <Flex p="1" gap="4" style={{ alignItems: "center", minWidth: "64px", justifyContent: "flex-end" }}>
                  {/* Delete Dropdown (only for non-default) */}
                  {!isDefault ? (
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <IconButton size="1" variant="ghost" color="gray">
                          <DotsHorizontalIcon width="16" height="16" />
                        </IconButton>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Content>
                        <DropdownMenu.Item color="red" onClick={() => setDeleteConfirmId(setting.id)}>
                          Delete
                        </DropdownMenu.Item>
                      </DropdownMenu.Content>
                    </DropdownMenu.Root>
                  ) : (
                    <div style={{ width: "24px" }} /> // Placeholder to maintain alignment
                  )}
                  {/* Expand/Collapse Toggle */}
                  <IconButton size="1" variant="ghost" color="gray" onClick={() => toggleCardExpansion(setting.id)}>
                    {isExpanded ? <ChevronUpIcon width="16" height="16" /> : <ChevronDownIcon width="16" height="16" />}
                  </IconButton>
                </Flex>
              </Flex>
            </Flex>

            {/* Expanded Content */}
            {isExpanded && (
              <Flex direction="column" gap="4" style={{ alignSelf: "stretch", alignItems: "flex-start" }}>
                <Separator size="4" orientation="horizontal" />

                {/* Setting Name (editable for non-default) */}
                {!isDefault && (
                  <>
                    <Flex direction="column" gap="2" style={{ alignSelf: "stretch" }}>
                      <Text size="2" weight="medium">Setting Name</Text>
                      <TextField.Root
                        value={formData.name || ""}
                        onChange={(e) => updateFormState(setting.id, "name", e.target.value)}
                        placeholder="e.g., Premium Locations"
                        style={{ maxWidth: 400 }}
                      />
                    </Flex>
                    <Separator size="4" orientation="horizontal" />
                  </>
                )}

                {/* Location Assignment (only for non-default settings) */}
                {!isDefault && (
                  <>
                    <Flex direction="column" gap="2" style={{ alignSelf: "stretch" }}>
                      <Text size="2" weight="medium">Assigned Locations</Text>
                      <Text size="1" color="gray">Select locations to use this AI setting (unassigned locations use default, disabled locations are assigned to other settings)</Text>
                      {availableLocations.length > 0 ? (
                        <div style={{ 
                          display: "grid", 
                          gridTemplateColumns: "repeat(3, 1fr)", 
                          gap: "var(--space-3)",
                          alignSelf: "stretch"
                        }}>
                          {availableLocations.map((loc) => {
                            const isAssignedToOther = isLocationAssignedToOther(loc.id, setting.id);
                            const assignedToName = isAssignedToOther ? getAssignedSettingName(loc.id, setting.id) : null;
                            const isChecked = (formData.location_ids || []).includes(loc.id);
                            
                            const checkboxContent = (
                              <Flex 
                                key={loc.id}
                                align="center" 
                                gap="2"
                                style={{ 
                                  padding: "var(--space-2)",
                                  borderRadius: "var(--radius-2)",
                                  backgroundColor: isAssignedToOther ? "var(--gray-a3)" : (isChecked ? "var(--accent-a3)" : "transparent"),
                                  opacity: isAssignedToOther ? 0.6 : 1,
                                  cursor: isAssignedToOther ? "not-allowed" : "pointer"
                                }}
                                onClick={() => !isAssignedToOther && toggleLocationSelection(setting.id, loc.id)}
                              >
                                <Checkbox 
                                  checked={isChecked}
                                  disabled={isAssignedToOther}
                                  onCheckedChange={() => !isAssignedToOther && toggleLocationSelection(setting.id, loc.id)}
                                />
                                <Text size="2" color={isAssignedToOther ? "gray" : undefined}>
                                  {loc.name}
                                </Text>
                              </Flex>
                            );

                            return isAssignedToOther ? (
                              <Tooltip key={loc.id} content={`Assigned to "${assignedToName}"`}>
                                {checkboxContent}
                              </Tooltip>
                            ) : checkboxContent;
                          })}
                        </div>
                      ) : (
                        <Text size="2" color="gray">No locations available</Text>
                      )}
                    </Flex>
                    <Separator size="4" orientation="horizontal" />
                  </>
                )}

                {isDefault && (
                  <>
                    <Callout.Root color="gray" size="1">
                      <Callout.Icon>
                        <InfoCircledIcon />
                      </Callout.Icon>
                      <Callout.Text>
                        This default setting applies to all locations not assigned to a custom setting.
                      </Callout.Text>
                    </Callout.Root>
                    <Separator size="4" orientation="horizontal" />
                  </>
                )}

                {/* Auto-Reply Toggle */}
                <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
                  <Flex direction="column" gap="1">
                    <Text size="2" weight="medium">Auto-Reply to New Reviews</Text>
                    <Text size="1" color="gray">
                      Automatically generate and post responses to new reviews
                    </Text>
                  </Flex>
                  <Switch 
                    checked={formData.auto_reply_enabled || false} 
                    onCheckedChange={(checked) => {
                      if (checked) {
                        // If turning on and nothing selected, select all by default
                        const currentRatings = formData.auto_reply_ratings || [];
                        if (!currentRatings.length) {
                          updateFormState(setting.id, "auto_reply_ratings", [1,2,3,4,5]);
                        }
                      } else {
                        // Turning off clears selected ratings for clarity
                        updateFormState(setting.id, "auto_reply_ratings", []);
                      }
                      updateFormState(setting.id, "auto_reply_enabled", checked);
                    }}
                    size="2"
                  />
                </Flex>
                <Separator size="4" orientation="horizontal" />

                {/* Auto-reply Star Ratings */}
                <Flex direction="column" gap="2" style={{ alignSelf: "stretch" }}>
                  <Text size="2" weight="medium">Auto-Reply Star Ratings</Text>
                  <Text size="1" color="gray">Select which star ratings should be auto-replied to (Google reviews only).</Text>
                  <Flex gap="2">
                    {[5,4,3,2,1].map((r) => {
                      const checked = (formData.auto_reply_ratings || []).includes(r);
                      return (
                        <Button 
                          key={r}
                          size="1"
                          variant={checked ? 'solid' : 'soft'}
                          color={checked ? 'blue' : 'gray'}
                          onClick={() => toggleAutoReplyRating(setting.id, r)}
                        >
                          {r}★
                        </Button>
                      );
                    })}
                  </Flex>
                </Flex>
                <Separator size="4" orientation="horizontal" />

                {/* Tone, Persona, Length Row */}
                <Flex gap="5" style={{ alignSelf: "stretch" }}>
                  <Flex direction="column" gap="2" style={{ flex: 1 }}>
                    <Text size="2" weight="medium">Response Tone</Text>
                    <Select.Root 
                      value={formData.tone_control || "friendly"} 
                      onValueChange={(value) => updateFormState(setting.id, "tone_control", value)}
                    >
                      <Select.Trigger style={{ width: "100%" }} />
                      <Select.Content>
                        <Select.Item value="friendly">Friendly</Select.Item>
                        <Select.Item value="professional">Professional</Select.Item>
                        <Select.Item value="warm_polite">Warm and Polite</Select.Item>
                        <Select.Item value="luxury_brand">Luxury Brand Style</Select.Item>
                        <Select.Item value="short_direct">Short and Direct</Select.Item>
                        <Select.Item value="empathetic">Empathetic</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </Flex>

                  <Flex direction="column" gap="2" style={{ flex: 1 }}>
                    <Text size="2" weight="medium">Response Persona</Text>
                    <Select.Root 
                      value={formData.persona || "owner"} 
                      onValueChange={(value) => updateFormState(setting.id, "persona", value)}
                    >
                      <Select.Trigger style={{ width: "100%" }} />
                      <Select.Content>
                        <Select.Item value="owner">Owner (I, my)</Select.Item>
                        <Select.Item value="team">Team (We, our)</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </Flex>

                  <Flex direction="column" gap="2" style={{ flex: 1 }}>
                    <Text size="2" weight="medium">Response Length</Text>
                    <Select.Root 
                      value={formData.default_length || "standard"} 
                      onValueChange={(value) => updateFormState(setting.id, "default_length", value)}
                    >
                      <Select.Trigger style={{ width: "100%" }} />
                      <Select.Content>
                        <Select.Item value="brief">Brief (1-2 sentences)</Select.Item>
                        <Select.Item value="standard">Standard (3-5 sentences)</Select.Item>
                        <Select.Item value="detailed">Detailed (5-7 sentences)</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </Flex>
                </Flex>
                <Separator size="4" orientation="horizontal" />

                {/* Greeting Format */}
                <Flex direction="column" gap="2" style={{ alignSelf: "stretch" }}>
                  <Text size="2" weight="medium">Greeting Format</Text>
                  <TextArea
                    value={formData.greeting_format || ""}
                    onChange={(e) => updateFormState(setting.id, "greeting_format", e.target.value)}
                    placeholder="Hi {{reviewer_first_name}},"
                    rows={2}
                  />
                  <Text size="1" color="gray">
                    Use {"{{reviewer_first_name}}"} for first name only, or {"{{reviewer_name}}"} for full name. Names are automatically capitalized.
                  </Text>
                </Flex>
                <Separator size="4" orientation="horizontal" />

                {/* Signature Format */}
                <Flex direction="column" gap="2" style={{ alignSelf: "stretch" }}>
                  <Text size="2" weight="medium">Signature Format</Text>
                  <TextArea
                    value={formData.signature_format || ""}
                    onChange={(e) => updateFormState(setting.id, "signature_format", e.target.value)}
                    placeholder={"Kind regards,\n{{business_name}} Team"}
                    rows={2}
                  />
                  <Text size="1" color="gray">
                    Use {"{{business_name}}"} to include the location name.
                  </Text>
                </Flex>
                <Separator size="4" orientation="horizontal" />

                {/* Negative Review Apology */}
                <Flex direction="column" gap="2" style={{ alignSelf: "stretch" }}>
                  <Flex align="center" gap="2">
                    <Text size="2" weight="medium">Negative Review Apology Script</Text>
                    <Badge color="orange" size="1">For 1-3 star reviews</Badge>
                  </Flex>
                  <TextArea
                    value={formData.negative_review_apology || ""}
                    onChange={(e) => updateFormState(setting.id, "negative_review_apology", e.target.value)}
                    rows={3}
                  />
                </Flex>
                <Separator size="4" orientation="horizontal" />

                {/* Additional Instructions */}
                <Flex direction="column" gap="2" style={{ alignSelf: "stretch" }}>
                  <Text size="2" weight="medium">Additional Instructions</Text>
                  <TextArea
                    value={formData.additional_instructions || ""}
                    onChange={(e) => updateFormState(setting.id, "additional_instructions", e.target.value)}
                    placeholder="Any specific guidelines for the AI..."
                    rows={3}
                  />
                </Flex>

                {/* Save Button - Bottom Right */}
                <Flex justify="end" style={{ alignSelf: "stretch" }}>
                  <Button 
                    onClick={() => handleSave(setting.id)}
                    disabled={!hasChanges(setting.id)}
                    loading={savingId === setting.id}
                  >
                    Save changes
                  </Button>
                </Flex>
              </Flex>
            )}
          </Card>
        );
      })}

      {/* Delete Confirmation Dialog */}
      <AlertDialog.Root open={deleteConfirmId !== null} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialog.Content>
          <AlertDialog.Title>Delete AI Setting</AlertDialog.Title>
          <AlertDialog.Description>
            Are you sure you want to delete &quot;{settings.find(s => s.id === deleteConfirmId)?.name}&quot;? This action cannot be undone.
            {settings.find(s => s.id === deleteConfirmId)?.locations_count > 0 && (
              <Text as="p" color="orange" size="2" mt="2">
                This setting has {settings.find(s => s.id === deleteConfirmId)?.locations_count} location(s) assigned. They will use the default setting after deletion.
              </Text>
            )}
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button 
                color="red" 
                onClick={() => handleDelete(deleteConfirmId)}
                loading={deleteSetting.isPending}
              >
                Delete
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      {/* Add Custom Setting Section */}
      {!showNewSettingForm ? (
        <Button 
          variant="soft" 
          onClick={() => setShowNewSettingForm(true)}
          style={{ alignSelf: "stretch" }}
        >
          <PlusIcon /> Add Custom Setting
        </Button>
      ) : (
        <Card style={{ flexShrink: 0 }}>
          <Flex direction="column" gap="3" p="4">
            <Text size="3" weight="medium">Create New AI Setting</Text>
            <Flex direction="column" gap="2">
              <Text size="2" weight="medium">Setting Name *</Text>
              <TextField.Root
                value={newSettingName}
                onChange={(e) => setNewSettingName(e.target.value)}
                placeholder="e.g., Premium Locations, VIP Response Style"
              />
            </Flex>
            <Flex gap="3" justify="end">
              <Button 
                variant="soft" 
                color="gray"
                onClick={() => {
                  setShowNewSettingForm(false);
                  setNewSettingName("");
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateNew}
                loading={isCreating}
                disabled={!newSettingName.trim()}
              >
                Create Setting
              </Button>
            </Flex>
          </Flex>
        </Card>
      )}
    </Flex>
  );
}
